// Review-finding reads shared by the Security surfaces: the detail spec for
// one finding's stored evidence, and the row shaping that turns graph rows
// into the finding objects the investigation views render.
//
// Timestamps and severity strings arrive from the analytics graph, so the
// normalizers here are the single place that lowercases enum text and turns
// ClickHouse's space-separated UTC stamps into epoch milliseconds.

const WINDOWS = Object.freeze([
    { id: "7d", label: "7 days", days: 7 },
    { id: "30d", label: "30 days", days: 30 },
    { id: "90d", label: "90 days", days: 90 },
]);
const DEFAULT_WINDOW = "30d";

const FINDING = "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING";
const F = (suffix, alias) => ({
    field: { stepAlias: "f", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_FINDING_${suffix}` },
    outputAlias: alias,
});

export function windowDays(windowId) {
    const w = WINDOWS.find((x) => x.id === windowId) ?? WINDOWS.find((x) => x.id === DEFAULT_WINDOW);
    // The graph refuses more than 90 days.
    return Math.min(w.days, 90);
}

function timeRangeForWindow(windowId) {
    return { relative: { durationSeconds: String(windowDays(windowId) * 86400) } };
}

// The detail read addresses immutable finding IDs, so it stays valid however
// the caller found them.
const FINDING_PAGE_SIZE = 50;
const DETAIL_FIELDS = [
    "TITLE", "RATIONALE", "BEHAVIOUR_CATEGORY", "BEHAVIOUR_DEFINITION",
    "CONTENT_CATEGORY", "CONTENT_DEFINITION", "POLICY_NOTE", "POLICY_CLAIM_REASONING",
    "NORM_NOTE", "INTENT_NOTE", "INTENT_CLAIM_REASONING", "CITATION_QUOTES",
    "BEHAVIOUR_CLAIM_RESULT", "BEHAVIOUR_CLAIM_REASONING",
];
const CITATION_METADATA_FIELDS = [
    "CITATION_STATUSES", "CITATION_PURPOSES", "CITATION_MESSAGE_IDS",
    "CITATION_REPRESENTATION_IDS", "CITATION_SOURCE_EVENT_IDS", "CITATION_PART_ORDINALS",
    "CITATION_SOURCE_STARTS", "CITATION_SOURCE_ENDS",
];

export function findingDetailSpec(windowId, traceId, findingIds, { citationMetadata = false } = {}) {
    if (!traceId || !findingIds.length || findingIds.length > FINDING_PAGE_SIZE) {
        throw new Error("Finding detail requires a Prompt Session and 1 to 50 finding IDs.");
    }
    return {
        root: { alias: "f", entity: FINDING },
        timeRange: timeRangeForWindow(windowId),
        filter: { andExpr: { children: [
            { pred: { field: F("TRACE_ID").field, op: "OPERATOR_EQ", value: traceId } },
            { pred: { field: F("ID").field, op: "OPERATOR_IN", value: findingIds } },
        ] } },
        output: { dimensions: [
            // Identity only, to stay below the planner's 32-column limit
            // including when citation metadata is available.
            F("ID", "finding_id"), F("TRACE_ID", "trace_id"), F("REPORT_ID", "report_id"),
            ...DETAIL_FIELDS.map((suffix) => F(suffix, suffix.toLowerCase())),
            ...(citationMetadata ? CITATION_METADATA_FIELDS.map((suffix) => F(suffix, suffix.toLowerCase())) : []),
        ] },
        limit: findingIds.length,
    };
}

function arrayCell(value) {
    if (Array.isArray(value)) return value;
    if (typeof value !== "string") return null;
    try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : null; }
    catch { return null; }
}

export function mergeFindingDetail(base, row) {
    const quotes = arrayCell(row.citation_quotes) ?? [];
    const columns = ["statuses", "purposes", "message_ids", "representation_ids", "source_event_ids", "part_ordinals", "source_starts", "source_ends"];
    const arrays = Object.fromEntries(columns.map((name) => [name, arrayCell(row[`citation_${name}`])]));
    const aligned = columns.every((name) => arrays[name]?.length === quotes.length);
    const uuid = (v) => typeof v === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(v);
    const citations = quotes.map((quote, i) => {
        const coordinates = aligned && ["message_ids", "representation_ids", "source_event_ids"].every((key) => uuid(arrays[key][i]))
            && ["part_ordinals", "source_starts", "source_ends"].every((key) => Number.isSafeInteger(arrays[key][i]) && arrays[key][i] >= 0)
            && arrays.source_ends[i] >= arrays.source_starts[i];
        return {
            quote: typeof quote === "string" ? quote : "",
            status: aligned ? arrays.statuses[i] : "",
            purpose: aligned ? arrays.purposes[i] : "",
            itemRef: "",
            source: coordinates ? {
                messageId: arrays.message_ids[i], representationId: arrays.representation_ids[i],
                sourceEventId: arrays.source_event_ids[i], partOrdinal: arrays.part_ordinals[i],
                start: arrays.source_starts[i], end: arrays.source_ends[i],
            } : null,
        };
    });
    const frame = (prefix, key = prefix) => ({
        status: base[`${key}Status`], reference: base[`${key}Reference`], note: str(row[`${prefix}_note`]),
        claim: { result: base[`${key}ClaimResult`] ?? "", reasoning: str(row[`${prefix}_claim_reasoning`]) },
    });
    return {
        ...base, title: str(row.title) || base.title, rationale: str(row.rationale),
        behaviourCategory: str(row.behaviour_category), behaviourDefinition: str(row.behaviour_definition),
        contentCategory: str(row.content_category), contentDefinition: str(row.content_definition),
        policy: frame("policy"), norms: frame("norm"), intent: frame("intent"),
        citations, citationMetadataUnavailable: !aligned || citations.some((c) => !c.source),
    };
}

// The probe's estimates on an analytics row, in the shape the review API
// serves them (reviewClient's adaptEstimates); null when the row has none.
export function findingEstimates(row) {
    const value = (raw) => (raw == null || raw === "" || !Number.isFinite(Number(raw)) ? null : Number(raw));
    const estimates = {
        version: str(row?.estimates_version),
        readerConfidence: value(row?.estimates_reader_confidence),
        independentConfidence: value(row?.estimates_independent_confidence),
        readerSeverity: value(row?.estimates_reader_severity),
        receivedConduct: value(row?.estimates_received_conduct),
    };
    return ["readerConfidence", "independentConfidence", "readerSeverity", "receivedConduct"].every((name) => estimates[name] == null) ? null : estimates;
}

// ClickHouse timestamps arrive as "2026-09-01 12:00:00.000" or RFC 3339.
export function parseTs(v) {
    if (v == null || v === "") return null;
    const s = String(v);
    // "YYYY-MM-DD HH:MM:SS[.fff]" carries no zone and is UTC; Date.parse would
    // read the space form as local time, so it is normalized first.
    const plain = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/.exec(s);
    const ms = plain ? Date.parse(`${plain[1]}T${plain[2]}Z`) : Date.parse(s);
    return Number.isFinite(ms) ? ms : null;
}

const str = (v) => (v == null ? "" : String(v));

export function normalizeFindingRow(row) {
    return {
        source: "review",
        findingId: str(row.finding_id),
        traceId: str(row.trace_id),
        reportId: str(row.report_id),
        endpointId: row.endpoint_id ? str(row.endpoint_id) : null,
        integrationId: row.integration_id ? str(row.integration_id) : null,
        reason: str(row.reason).toLowerCase(),
        evidenceStatus: str(row.evidence_status).toLowerCase(),
        behaviourSeverity: str(row.behaviour_severity).toLowerCase(),
        contentSensitivity: str(row.content_sensitivity).toLowerCase(),
        policyStatus: str(row.policy_status).toLowerCase(),
        policyReference: str(row.policy_reference),
        policyClaimResult: str(row.policy_claim_result).toLowerCase(),
        normStatus: str(row.norm_status).toLowerCase(),
        normReference: str(row.norm_reference),
        intentStatus: str(row.intent_status).toLowerCase(),
        intentReference: str(row.intent_reference),
        intentClaimResult: str(row.intent_claim_result).toLowerCase(),
        verifierStatus: str(row.verifier_status).toLowerCase(),
        citationCount: Number(row.citation_count ?? 0),
        targetCitationCount: Number(row.target_citation_count ?? 0),
        profileVersion: Number(row.profile_version ?? 0),
        modelConfigurationId: str(row.model_configuration_id),
        providerModelIdentifier: str(row.provider_model_identifier),
        completedAtMs: parseTs(row.completed_at),
        title: row.title != null ? str(row.title) : null,
    };
}
